/**
 * WEEX timeframe configuration and interval mapping.
 *
 * WEEX v2 candle `period` values mirror the production connector
 * (gitlab.1wq.net/mm/111-connman → connector/weex/common/enum.go):
 *   1m, 5m, 15m, 30m, 1h, 4h, 12h, 1d, 1w, 1M
 * (note: no 3m and no 6h).
 *
 * @see https://api-spot.weex.com/api/v2/market/candles
 */

import {
  registerExchangeTimeframes,
  resolutionToSeconds,
  type TimeframeConfig,
} from '../../lib/timeframes';

/**
 * WEEX supported kline intervals (period values).
 */
export const WEEX_TIMEFRAMES: TimeframeConfig = {
  native: ['1m', '5m', '15m', '30m', '1h', '4h', '12h', '1d', '1w', '1M'],
  synthetic: ['1s'], // Use ticker for 1s
  minIntervalSeconds: 60,
};

// Register with central registry
registerExchangeTimeframes('weex', WEEX_TIMEFRAMES);

/**
 * Maps TradingView resolution to WEEX `period` format.
 */
export const mapToWeexInterval = (resolution: string): string => {
  const seconds = resolutionToSeconds(resolution);

  if (seconds <= 60) return '1m';
  if (seconds === 300) return '5m';
  if (seconds === 900) return '15m';
  if (seconds === 1800) return '30m';
  if (seconds === 3600) return '1h';
  if (seconds === 14400) return '4h';
  if (seconds === 43200) return '12h';
  if (seconds === 86400) return '1d';
  if (seconds === 604800) return '1w';
  if (seconds === 2592000) return '1M';

  // Default to closest smaller native interval
  if (seconds < 300) return '1m';
  if (seconds < 900) return '5m';
  if (seconds < 1800) return '15m';
  if (seconds < 3600) return '30m';
  if (seconds < 14400) return '1h';
  if (seconds < 43200) return '4h';
  if (seconds < 86400) return '12h';
  if (seconds < 604800) return '1d';
  if (seconds < 2592000) return '1w';

  return '1M';
};
